/**
 * Represents a notification manager that interacts with the OneSignal API.
 */
class NotificationManager {
    /**
     * Represents a Notifications object.
     * @constructor
     */
    constructor() {
        this.window = window;
    }

    /**
     * Sets the external user ID for OneSignal.
     *
     * @param {string} externalId - The external user ID to set.
     * @returns {Promise<void>} - A promise that resolves when the external user ID is set.
     */
    async setExternalUserId(externalId) {
        if (!this.window.OneSignal) return;

        await this.window.OneSignal.push(() => {
            this.window.OneSignal.login(externalId);
        });
    }

    /**
     * Sends a tag to OneSignal.
     * @param {string} key - The tag key.
     * @param {string} value - The tag value.
     * @returns {Promise<void>} - A promise that resolves when the tag is sent.
     */
    async sendTag(key, value) {
        if (!this.window.OneSignal) return;

        await this.window.OneSignal.push(() => {
            this.window.OneSignal.User.addTag(key, value);
        });
    }

    /**
     * Sends tags to OneSignal.
     * @param {Object} tags - The tags to be sent.
     * @returns {Promise<void>} - A promise that resolves when the tags are sent.
     */
    async sendTags(tags) {
        if (!this.window.OneSignal) return;
    
        await this.window.OneSignal.push(async () => {
            try {
                await this.window.OneSignal.User.addTags(tags);
            } catch (error) {
                console.error('Error sending tags:', error);
            }
        });
    }

    /**
     * Removes a tag from OneSignal.
     *
     * @param {string} key - The key of the tag to be removed.
     * @returns {Promise<void>} - A promise that resolves when the tag is successfully removed.
     */
    async removeTag(key) {
        if (!this.window.OneSignal) return;

        await this.window.OneSignal.push(() => {
            this.window.OneSignal.User.removeTag(key);
        });
    }

    /**
     * Removes the specified tags from OneSignal.
     * @param {Array<string>} keys - The keys of the tags to be removed.
     * @returns {Promise<void>} A promise that resolves when the tags are successfully removed.
     */
    async removeTags(keys) {
        if (!this.window.OneSignal) return;
    
        await this.window.OneSignal.push(async () => {
            try {
                this.window.OneSignal.User.removeTags(keys);
            } catch (error) {
                console.error('Error removing tags:', error);
            }
        });
    }

    /**
     * Retrieves the tags associated with the user from OneSignal.
     * @returns {Promise<Object>} A promise that resolves with the tags object.
     */
    async getTags() {
        if (!this.window.OneSignal) return;
    
        try {
            const tags = await this.window.OneSignal.User.getTags();
            return tags;
        } catch (error) {
            console.error('Error retrieving tags:', error);
        }
    }

    /**
     * Sends location data to the server.
     *
     * @param {string} address - The address of the location.
     * @param {string} country - The country of the location.
     * @returns {Promise<void>} - A promise that resolves when the location data is sent.
     */
    async sendLocationData(address, country) {
        if (!this.window.OneSignal) return;

        const tags = await this.getTags() ?? {};

        // Helper function to update tags with unique values
        const updateTagsWithUniqueValues = (tagKey, value) => {
            if (value) {
                try {
                    const existingTags = tags[tagKey] ? JSON.parse(tags[tagKey]) : [];
                    const updatedTags = new Set([...existingTags, value]); // Use Set for uniqueness
                    tags[tagKey] = JSON.stringify([...updatedTags]);
                } catch (error) {}
            }
        };

        updateTagsWithUniqueValues('locations', address);
        updateTagsWithUniqueValues('countries', country);

        this.sendTags(tags);
    }

    /**
     * Sends location data to the server.
     *
     * @param {string} address - The address of the location.
     * @returns {Promise<void>} - A promise that resolves when the location data is sent.
     */
    async updateLocationTags(address) {

        const tags = (await this.getTags()) ?? [];

        // Get the location data from local storage
        let locationData = localStorage.getItem('locationData');

        // Parse the data if it exists, or initialize an empty object if it doesn't
        locationData = locationData ? JSON.parse(locationData) : {};
        
        if(address) {
            // Check if the address exists in the data
            if (locationData[address]) {
                // If the address exists, increment the count and update the timestamp
                locationData[address].count++;
                locationData[address].timestamp = Date.now(); // Update the timestamp
            } else {
                // If the address does not exist, add it with a count of 1 and a timestamp
                locationData[address] = { count: 1, timestamp: Date.now() };
            }
        }
        
        // Save the updated data back to local storage
        localStorage.setItem('locationData', JSON.stringify(locationData));
        
        // Convert the location data object to an array of [address, count] pairs
        let locationArray = Object.entries(locationData);
        
        // Sort the array based on count in descending order; use timestamp to break ties
        locationArray.sort((a, b) => {
            if (b[1].count === a[1].count) {
                return b[1].timestamp - a[1].timestamp; // If counts are the same, prioritize newer locations
            }
            return b[1].count - a[1].count;
        });

        // Get the top 3 locations
        let topLocations = locationArray.slice(0, 3);
        
        // Update the tags object with the top 3 locations
        topLocations.forEach((location, index) => {
            tags[`location${index + 1}`] = location[0]; // Only add the address (location[0])
        });
        
        this.sendTags(tags);
    }

    /**
     * Updates the page tags based on the provided topic.
     * @param {string} topic - The topic to update the page tags with.
     * @returns {Promise<void>} - A promise that resolves once the page tags have been updated.
     */
    async updatePageTags(topic) {
        
        const minVisits = 1;
        const maxTopics = 5;
    
        // Get the visits object from localStorage or initialize it
        let visits = JSON.parse(localStorage.getItem('topicVisits')) || {};
    
        if (topic && typeof localStorage !== 'undefined') {
            // If topic is passed, increment the visit count and update the timestamp
            visits[topic] = visits[topic]
                ? { count: visits[topic].count + 1, timestamp: Date.now() }
                : { count: 1, timestamp: Date.now() };
    
            // Update localStorage with the new visit count
            localStorage.setItem('topicVisits', JSON.stringify(visits));
        }
    
        // Convert the visits object to an array of [key, value] pairs
        const visitsArray = Object.entries(visits);
    
        // Filter, sort by count and timestamp, and limit the array
        const limitedFilteredSortedVisits = visitsArray
            .filter(([_, value]) => value.count >= minVisits)
            .sort((a, b) => {
                if (b[1].count !== a[1].count) {
                    return b[1].count - a[1].count; // Sort by count
                }
                return b[1].timestamp - a[1].timestamp; // Sort by latest timestamp if counts are equal
            })
            .slice(0, maxTopics);
    
        // Convert the array back to an object with just the count values
        const sortedVisitsObject = Object.fromEntries(
            limitedFilteredSortedVisits.map(([key, value]) => [key, value.count])
        );
        
        const whiteListedTags = ['location1', 'location2', 'location3', 'business_area', 'jobfeed_type'];
    
        // Fetch existing tags
        const tags = await this.getTags();
    
        // Create a new object for the tags to be sent, including tags to remove
        const updatedTags = Object.keys(tags).reduce((acc, key) => {
            if (whiteListedTags.includes(key)) {
                acc[key] = tags[key];
            } else {
                acc[key] = ''; // Set the value to an empty string to remove the tag
            }
            return acc;
        }, {});
        
        // Merge the visits data into the tags to be sent
        const finalTags = { ...updatedTags, ...sortedVisitsObject };
    
        // Send all tags in a single sendTags call, including the ones marked for removal
        await this.sendTags(finalTags);
    }
    
}

export default NotificationManager;
